import { useQuery } from '@tanstack/react-query';

import type { AxiosInstance } from 'axios';
import type { EventDetail } from '@/pages/events/model/types';
import type { StatusBoardApiEnvelope } from '@/shared/api/status-board';

import { useStatusBoardApi } from '@/shared/api/status-board';
import { useRegionEndpoint } from '@/shared/hooks';

export interface GetConsoleEventRequest {
  eventId: string;
}

/**
 * Status Board 이벤트 상세를 조회한다.
 * @description
 * - `/api/console/events/{id}` 엔드포인트를 호출하고 응답 래퍼의 `data`를 언래핑한다.
 */
export const getConsoleEvent = async (
  instance: AxiosInstance,
  { eventId }: GetConsoleEventRequest,
): Promise<EventDetail> => {
  const response = await instance.get<StatusBoardApiEnvelope<EventDetail>>(
    `/api/console/events/${eventId}`,
  );

  return response.data.data;
};

/**
 * 이벤트 상세 조회 React Query 훅.
 * @description
 * - `eventId`가 없으면 요청을 비활성화한다.
 * @example
 * const { data, isLoading } = useGetConsoleEvent({ eventId });
 */
const useGetConsoleEvent = ({ eventId }: GetConsoleEventRequest) => {
  const { instance } = useStatusBoardApi();
  const { currentRegion } = useRegionEndpoint();

  return useQuery({
    queryKey: ['useGetConsoleEvent', currentRegion, eventId],
    queryFn: () => getConsoleEvent(instance, { eventId }),
    enabled: Boolean(eventId),
  });
};

export default useGetConsoleEvent;
